import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import '../styles/Collection.css';
import { Container } from 'react-bootstrap';
import Breadcrumbs from './Breadcrumbs';
import usePageNav from '../hooks/usePageNav';
import getNavigationInitialState from '../initialState/navigation';
import SearchPageSidebar from './SearchPageSidebar';
import SearchPageProductList from './SearchPageProductList';
import PageNavCurPageIndicator from './PageNavCurPageIndicator';
import PageNav from './PageNav';
import { LoadingContext } from './context/LoadingContext';
import usePrefetch from '../hooks/usePrefetch';
import SEO from './SEO';
import { bool, object } from 'prop-types';
import useSearch from '../hooks/useSearch';
import NotFound from './NotFound';
import hasStoreName from '../helpers/hasStoreName';
import AppContext from './context/AppContext';
import SearchPageSidebarSkeleton from './SearchPageSidebarSkeleton';
import { Fragment } from 'react';
import CollectionList from './CollectionList';
import pluralize from 'pluralize';
import constants from '../constants.json' assert { type: 'json' };
import useDynamicPath from '../hooks/useDynamicPath';


/** Helpers */
const toProperCase = (str) => str.at(0).toUpperCase() + str.substring(1).toLowerCase();
const getValidatedTagPrefix = (tagPrefix) => tagPrefix
    .split('-')
    .map((p) => p.split(' '))
    .reduce((prev, curr) => prev.concat(curr), [])
    .filter((p) => p.trim() !== '') // filter out empty strings
    .map((p) => toProperCase(p))
    .join(' ');

// Collection fetching func.
const fetchCollectionData = async (handle = '') => {
    const response = await fetch(new URL(`/api/collections/handle/${handle}`, process.env.APP_URL));
    const data = await response.json();
    return data?.collection ?? null;
};

export default function Collection({ isMobile, searchBoxRef }) {
    const { appContext } = useContext(AppContext);
    const collectionPath = useDynamicPath({ pathName: constants.DYNAMIC_PATHS.COLLECTION_PATH });
    const collectionBasePath = collectionPath[0].pathBeforeParams;
    const { handle, tagFilter } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { searchQuery, updateSearchParams } = useSearch();
    const [searchLoaded, setSearchLoaded] = useState(false);
    const [searchLoading, setSearchLoading] = useState(true);
    const [productList, setProductList] = useState([]);
    const [featuredProductList, setFeaturedProductList] = useState([]);
    const [hierarchy, setHierarchy] = useState({});
    // Loading state
    const {
        startLoading,
        finishLoading,
    } = useContext(LoadingContext);
    /* Pagination code */
    const containerRef = useRef(null);
    const [pagesData, setPagesData] = useState(getNavigationInitialState());
    const handlePageChange = (newCurPage) => {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('page', parseInt(newCurPage));
        setSearchParams(newSearchParams);
    };
    const {
        curPage,
        curRange,
        nextRange,
        generatedPagesData,
        paginateUser,
    } = usePageNav({
        containerRef,
        pagesData,
        initialCurPage: Math.min(parseInt(searchParams.get('page') || 0, 0)),
        onUpdate: handlePageChange,
    });
    /* End pagination code */
    const {
        isLoaded: collectionLoaded,
        data: collectionData,
    } = usePrefetch({
        key: 'collection',
        resourceKey: 'handle',
        resourceValue: handle,
        prefetchFn: async () => {
            await startLoading();
            const data = await fetchCollectionData(handle);
            await finishLoading();
            return data;
        }
    });
    // Product fetching func.
    /* Search functionality */
    const handleSearch = useCallback(async () => {
        try {
            if (isNaN(collectionData.id) || collectionData.id === null)
                return setSearchLoading(false);
            setSearchLoading(true);
            let validatedTagFilter = [];
            if (tagFilter && tagFilter.indexOf('_') !== -1) {
                // Split tag prefix string before and after the first underscore
                const tagPrefix = tagFilter.slice(0, tagFilter.indexOf('_')).trim();
                const tagValue = tagFilter.slice(tagFilter.indexOf('_') + 1).trim();
                if (tagPrefix && tagValue) {
                    // Split tag prefix provided in the URL by the hyphens
                    // Capitalize first letter of each segment
                    // Join with spaces
                    const tagPrefixValidated = getValidatedTagPrefix(tagPrefix);
                    // Add tag value as-is
                    // Add tag value lowercase
                    // Add tag value capitalized first letter
                    const tagValues = new Set([
                        tagValue,
                        tagValue.toLowerCase(),
                        tagValue.toUpperCase(),
                        toProperCase(tagValue),
                    ]);
                    [...tagValues.values()].forEach((val) => {
                        validatedTagFilter.push(tagPrefixValidated, val);
                    });
                }
            }
            const tags = searchParams.getAll('t');
            tags.forEach((tag) => {
                if (tag.indexOf(',') !== -1) {
                    const tagPrefix = tag.slice(0, tags.indexOf(',')).trim();
                    const tagValue = tag.slice(tags.indexOf(',') + 1).trim();
                    if (tagPrefix && tagValue) {
                        const tagPrefixValidated = getValidatedTagPrefix(tagPrefix);
                        const tagValues = new Set([
                            tagValue,
                            tagValue.toLowerCase(),
                            tagValue.toUpperCase(),
                            toProperCase(tagValue),
                        ]);
                        [...tagValues.values()].forEach((val) => {
                            validatedTagFilter.push(tagPrefixValidated, val);
                        });
                    }
                }
            });
            const params = new URLSearchParams(Object.assign(
                {},
                searchQuery, {
                    c: [collectionData.id],
                    ...validatedTagFilter && ({ t: validatedTagFilter }),
                })).toString();
            // Products
            const productResponse = await fetch(new URL('api/products/filter?' + params, process.env.APP_URL));
            const productData = await productResponse.json();
            setProductList(productData.products ?? []);
            if (productData.pageInfo) {
                setPagesData({
                    limit: productData.pageInfo.limitPerPage,
                    count: productData.pageInfo.totalObjects,
                    pages: productData.pageInfo.totalPages,
                    availableTagPrefixes: productData.pageInfo.availableTagPrefixes ?? [],
                    availableTagFilters: productData.pageInfo.availableTagFilters ?? [],
                    availableVendors: productData.pageInfo.availableVendors ?? [],
                    productMaxPrice: productData.pageInfo.productMaxPrice,
                });
            }
            // Update Search
            setSearchLoaded(true);
            setSearchLoading(false);
        } catch (e) {
            console.error(e);
            setSearchLoaded(false);
        }
    }, [searchQuery, collectionData, tagFilter]);
    // Get featured products
    const fetchFeaturedProducts = useCallback(async () => {
        try {
            if (isNaN(collectionData.id) || collectionData.id === null)
                return;
            const response = await fetch(new URL(`/api/collections/${collectionData.id}/products/featured`, process.env.APP_URL));
            const data = await response.json();
            setFeaturedProductList(data.products?.map((product) => Object.assign({}, product, {
                isFeatured: true,
            })) ?? []);
        } catch (e) {
            console.error(e);
        }
    }, [collectionData]);
    useEffect(() => {
        if (collectionLoaded
            && collectionData) {
            fetchFeaturedProducts();
        }
    }, [collectionLoaded, collectionData]);
    /* End search functionality */
    // Fetch hierarchy
    const fetchHierarchy = useCallback(async () => {
        try {
            const response = await fetch(new URL(`/api/collections/${collectionData.id}/hierarchy`, process.env.APP_URL));
            const data = await response.json();
            setHierarchy(data.hierarchy ?? {});
        } catch (e) {
            console.error(e);
        }
    }, [collectionData]);
    useEffect(() => {
        if (collectionLoaded
            && collectionData) {
            fetchHierarchy();
        }
    }, [collectionLoaded, collectionData]);
    // End fetch hierarchy
    useEffect(() => {
        if (collectionLoaded
            && collectionData) {
            handleSearch();
        }
        if (collectionLoaded && !collectionData) {
            setSearchLoading(false);
        }
    }, [collectionLoaded, collectionData, handleSearch]);
    /** Add store name to title if not exists */
    const title = useMemo(() => {
        if (!collectionLoaded
            || !collectionData)
            return '';
        let title = collectionData.seoTitle ?? collectionData.title;
        if (!hasStoreName(title, appContext.config.storeName)) {
            title = `${title} - ${appContext.config.storeName}`;
        }
        return title;
    }, [collectionLoaded, collectionData]);
    if (collectionLoaded === true && !collectionData)
        return (<NotFound />);

    const collectionPageRef = useCallback(() => {
        searchBoxRef.current?.enableAlterPath();
    }, []);

    return (<Container
        style={{ padding: '0' }}
        ref={collectionPageRef}
        fluid>
        <div
            className={'page--section'}>
            {(collectionLoaded === true && collectionData !== null) && (<>
                {Object.keys(collectionData).length > 0 && (<>
                    <SEO
                        seoTitle={title}
                        seoDescription={collectionData.seoDescription ?? null}
                        canonicalUrl={new URL(`${collectionBasePath}/${collectionData.handle}`, process.env.APP_URL).toString()}
                        image={collectionData.imageUrl ?? null}
                        breadsArr={[
                            {
                                title: 'Home',
                                handle: '/',
                            },
                            {
                                title: 'Collections',
                                handle: `${collectionBasePath}/`,
                            },
                            {
                                title: collectionData.title,
                                handle: `${collectionBasePath}/${collectionData.handle}`,
                            },
                        ]}
                    />
                    <div
                        className={'collection--heading'}>
                        <Breadcrumbs
                            breadcrumbList={[
                                [
                                    collectionData.title,
                                    `${collectionBasePath}/${collectionData.handle}`,
                                ],
                            ]} />
                        <h1
                            className={'collection--title'}>{collectionData.title}</h1>
                        {Object.entries(hierarchy).map(([type, data]) => {
                            if (type === 'default' || !Array.isArray(data) || !data.length)
                                return null;
                            data = data.filter((c) => c.handle !== collectionData.handle);
                            if (!data.length)
                                return null;
                            return (<Fragment
                                key={type}>
                                <h2>{pluralize(type)}</h2>
                                <CollectionList
                                    showImage={false}
                                    collections={data}
                                />
                            </Fragment>);
                        })}
                    </div>
                    <div
                        className={'search-page--inner'}>
                        {((Array.isArray(pagesData.availableTagFilters)
                            && pagesData.availableTagFilters.length > 0
                        ) || (Array.isArray(pagesData.availableVendors)
                            && pagesData.availableVendors.length > 0
                        )) && (<>
                            {searchLoading === false && (<SearchPageSidebar
                                isMobile={isMobile}
                                searchQuery={searchQuery}
                                setSearchQuery={updateSearchParams}
                                vendorList={pagesData.availableVendors ?? []}
                                tagPrefixes={pagesData.availableTagPrefixes ?? []}
                                tagList={pagesData.availableTagFilters ?? []}
                                productMaxPrice={pagesData.productMaxPrice}
                                searchLoading={searchLoading}
                                productCount={productList.length}
                            />)}
                        </>)}
                        {searchLoading === true && (<SearchPageSidebarSkeleton
                            isMobile={isMobile} />)}
                        <SearchPageProductList
                            ref={containerRef}
                            isMobile={isMobile}
                            productList={[...featuredProductList, ...productList]}
                            searchLoading={searchLoading}
                            collectionHandle={handle}>
                            <PageNavCurPageIndicator
                                curRange={curRange}
                                nextRange={nextRange}
                                totalCount={pagesData.count}
                                resourceType={'products'}
                                noStyle={true}
                            />
                        </SearchPageProductList>
                    </div>
                    {productList.length > 0 && (<PageNav
                        curPage={curPage}
                        totalPages={pagesData.pages}
                        generatedPagesData={generatedPagesData}
                        paginateUser={paginateUser}
                    />)}
                    <br />
                    <div
                        className={'collection--body'}
                        dangerouslySetInnerHTML={{ __html: collectionData.descriptionHtml }}></div>
                </>)}
            </>)}
        </div>
    </Container>);
}

Collection.propTypes = {
    isMobile: bool,
    searchBoxRef: object,
};
